import React from 'react';
import moment from 'moment';

import Button from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import Select from 'components/Select/Select';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';

import { getAccountBalanceTransfers, getAccountBalanceTransferDetail, confirmAccountBalanceTransfer } from 'api/accountbalancetransfers';
import ImageViewer from 'components/ImageViewer/ImageViewer';

const PAGE_SIZE = 20;
const IMAGE_PROXY_URL = 'https://kot-image-proxy.coinflipsyndicate.online:8443/39bd83260b149297453aa06d71090d57';

const AccountBalanceTransfers: React.FC = () => {
    const queryClient = useQueryClient();
    const [selectType, setSelectType] = React.useState('pending');
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(PAGE_SIZE);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [modalOpen, setModalOpen] = React.useState(false);

    const { data, isLoading } = useQuery(['accountbalancetransfers', selectType, page, pageSize], getAccountBalanceTransfers);

    const detailQuery = useQuery(['accountbalancetransfer', selectedId], getAccountBalanceTransferDetail, {
        enabled: !!selectedId,
    });

    const confirmMutation = useMutation(() => confirmAccountBalanceTransfer(selectedId!), {
        onSuccess: () => {
            queryClient.invalidateQueries(['accountbalancetransfer', selectedId]);
            queryClient.invalidateQueries(['accountbalancetransfers']);
        }
    });

    // Prefetch скриншотов для pending записей
    const prefetchImages = React.useRef<HTMLImageElement[]>([]);
    React.useEffect(() => {
        if (selectType !== 'pending' || !data) return;
        const items = data?.data ?? data ?? [];
        const images: HTMLImageElement[] = [];
        items.forEach((item: any) => {
            // comment содержит URL скриншота кэшаута
            const commentMatch = item.comment?.match(/(https?:\/\/[^\s]+)/);
            if (commentMatch) {
                const img = new Image();
                img.src = `${IMAGE_PROXY_URL}/image?url=${encodeURIComponent(commentMatch[0])}`;
                images.push(img);
            }
            // deposit_scr — URL скриншота депозита
            const depositMatch = item.deposit_scr?.match(/(https?:\/\/[^\s]+)/);
            if (depositMatch) {
                const img = new Image();
                img.src = `${IMAGE_PROXY_URL}/image?url=${encodeURIComponent(depositMatch[0])}`;
                images.push(img);
            }
        });
        prefetchImages.current = images;
    }, [data, selectType]);

    const handleSelectChange = (value: string) => {
        setSelectType(value);
        setPage(0);
    };

    const goToDetail = ({ data }: any) => {
        setSelectedId(data.id);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const formatDate = ({ value }: { value: string }) => {
        return moment(value).format('DD MMM YYYY HH:mm');
    };

    const getStatusClass = ({ value }: { value: boolean }) => {
        return value ? 'bg-green-200' : 'bg-amber-500';
    };

    const formatStatus = ({ value }: { value: boolean }) => {
        return value ? 'processed' : 'pending';
    };

    const formatAmount = ({ data }: any) => {
        const amount = parseFloat(data.amount);
        return `${amount} ${data.chip_currency_name}`;
    };

    const formatRooms = ({ data }: any) => {
        return `${data.cashout_room_name} → ${data.deposit_room_name}`;
    };

    const rows = data?.data ?? data ?? [];
    const rowCount = data?.count ?? 0;
    const detail = detailQuery.data;

    return (
        <div className="space-y-4">
            <div className="flex lg:flex-row flex-col lg:space-x-8 lg:space-y-0 space-y-4 mb-8">
                <div className="w-full">
                    <Select
                        label=""
                        options={['pending', 'processed']}
                        value={selectType}
                        onChange={handleSelectChange}
                        multiple={false}
                    />
                </div>
            </div>

            <div className="w-full relative shadow-md rounded overflow-hidden" style={{ height: 800 }}>
                <AgGridReact
                    rowData={rows}
                    overlayNoRowsTemplate={isLoading ? '<span></span>' : '<span>Нет данных</span>'}
                    defaultColDef={{
                        flex: 1,
                        sortable: true,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: { suppressFilterButton: true }
                    }}
                    onRowClicked={goToDetail}
                >
                    <AgGridColumn headerName="Дата создания" field="created_datetime" sort="desc" valueFormatter={formatDate} filter />
                    <AgGridColumn headerName="Игрок" field="player_username" filter />
                    <AgGridColumn headerName="Румы" valueFormatter={formatRooms} />
                    <AgGridColumn headerName="Сумма" valueFormatter={formatAmount} cellStyle={{ fontVariantNumeric: 'tabular-nums' }} />
                    <AgGridColumn headerName="Статус" field="is_processed" valueFormatter={formatStatus} cellClass={getStatusClass} />
                </AgGridReact>
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.8)' }}>
                        <CircularProgress />
                    </div>
                ) : null}
            </div>

            <TablePagination
                component="div"
                count={rowCount}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(0); }}
            />

            <Dialog open={modalOpen} onClose={handleCloseModal} TransitionProps={{ onExited: () => setSelectedId(null) }} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Перевод (румы) №{selectedId}
                    <IconButton onClick={handleCloseModal} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ position: 'relative', minHeight: 450 }}>
                    {detailQuery.isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white" style={{ zIndex: 1 }}>
                            <CircularProgress />
                        </div>
                    )}
                    <div style={{ opacity: detailQuery.isLoading ? 0 : 1 }}>
                        <p className="text-lg font-light mb-4">
                            Дата создания: {moment(detail?.created_datetime).format('DD MMM YYYY HH:mm')}
                        </p>
                        <p className="text-lg font-light mb-4">
                            Игрок: {detail?.player_username}
                        </p>
                        <div className="bg-gray-50 border border-gray-200 rounded py-2 mb-4 inline-block" style={{ marginLeft: '-0.75rem', paddingLeft: '0.75rem', paddingRight: '0.75rem' }}>
                            <p className="text-lg font-light mb-1">
                                Из рума: <span className="font-medium">{detail?.cashout_room_name} ({detail?.cashout_room_balance} {detail?.chip_currency_name})</span>
                            </p>
                            <p className="text-lg font-light mb-1">
                                В рум: <span className="font-medium">{detail?.deposit_room_name} ({detail?.deposit_room_balance} {detail?.chip_currency_name})</span>
                            </p>
                            <p className="text-lg font-light">
                                Сумма: <span className="font-medium">{detail?.amount} {detail?.chip_currency_name}</span>
                            </p>
                        </div>
                        {(() => {
                            const transferOverlay = (
                                <>
                                    <span>Из рума: <b>{detail?.cashout_room_name} ({detail?.cashout_room_balance} {detail?.chip_currency_name})</b></span>
                                    <span style={{ margin: '0 12px' }}>·</span>
                                    <span>В рум: <b>{detail?.deposit_room_name} ({detail?.deposit_room_balance} {detail?.chip_currency_name})</b></span>
                                    <span style={{ margin: '0 12px' }}>·</span>
                                    <span>Сумма: <b>{detail?.amount} {detail?.chip_currency_name}</b></span>
                                </>
                            );
                            return (
                                <table className="text-lg font-light" style={{ borderSpacing: '0 14px', borderCollapse: 'separate', marginBottom: 2 }}>
                                    <tbody>
                                        {/(https?:\/\/[^\s]+)/.test(detail?.comment || '') && (
                                            <tr>
                                                <td style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>Кэшаут:</td>
                                                <td><ImageViewer url={detail.comment.match(/(https?:\/\/[^\s]+)/)[0]} label="Скрин" overlay={transferOverlay} /></td>
                                            </tr>
                                        )}
                                        {detail?.cashout_screenshot && (
                                            <tr>
                                                <td style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>Скриншот кэшаута:</td>
                                                <td><ImageViewer url={detail.cashout_screenshot} label="Скрин" overlay={transferOverlay} /></td>
                                            </tr>
                                        )}
                                        {/(https?:\/\/[^\s]+)/.test(detail?.deposit_scr || '') && (
                                            <tr>
                                                <td style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>Депозит:</td>
                                                <td><ImageViewer url={detail.deposit_scr.match(/(https?:\/\/[^\s]+)/)[0]} label="Скрин" overlay={transferOverlay} /></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            );
                        })()}
                        <p className="text-lg font-light mb-4">
                            Статус: <span className={`px-2 rounded ${detail?.is_processed ? 'bg-green-200' : 'bg-amber-500'}`} style={{ paddingTop: '1.2px', paddingBottom: '1.2px' }}>{detail?.is_processed ? 'processed' : 'pending'}</span>
                        </p>
                        {detail?.processed_datetime && (
                            <p className="text-lg font-light mb-4">
                                Дата обработки: {moment(detail.processed_datetime).format('DD MMM YYYY HH:mm')}
                            </p>
                        )}
                        {detail && !detail.is_processed && (
                            <div className="pt-4 text-right">
                                <Button
                                    variant="contained"
                                    color="success"
                                    loading={confirmMutation.isLoading}
                                    onClick={() => confirmMutation.mutate()}
                                >
                                    Подтвердить
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AccountBalanceTransfers;
